import { useCallback, useEffect, useRef, useState } from "react";
import { Upload, Link2, FileText, Camera, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { isMobile } from "@/lib/device";
import { cn } from "@/lib/utils";

/**
 * Unified recipe input module.
 *
 * Three equal input methods (Upload / Link / Text) presented as a segmented
 * control. Auto-detects pasted content (URL → Link mode, long text → Text
 * mode), remembers the last-used mode across sessions, and exposes a single
 * "Clean recipe" CTA for the manual flows.
 *
 * Designed to be the single source of truth for recipe ingest — reuse this
 * everywhere, only varying the headline copy.
 */

export type RecipeInputMode = "upload" | "link" | "text";

type Props = {
  /** Optional override for the heading. Default: "Add a recipe". */
  headline?: string;
  /** Optional override for the subhead. */
  subhead?: string;
  /** Called when the user submits a URL. */
  onSubmitUrl: (url: string) => void;
  /** Called when the user submits pasted text. */
  onSubmitText: (text: string) => void;
  /** Called with selected files (images, screenshots, or PDFs). */
  onSubmitFiles: (files: File[], fromCamera?: boolean) => void;
  /**
   * If true, swallow page-level paste events to auto-detect input type.
   * Default true. Set false when this component is mounted but a parent
   * handles paste differently (e.g. multiple instances on a page).
   */
  enablePagePaste?: boolean;
  /** Pre-fill the URL field (e.g. when restoring state). */
  initialUrl?: string;
  /** Pre-fill the text field. */
  initialText?: string;
  /**
   * Force the input to open on a specific mode, overriding the persisted
   * "last used" mode. Useful when navigating in from an error recovery
   * action (e.g. "Paste the recipe text instead").
   */
  forceMode?: RecipeInputMode;
  /** Compact density (smaller padding, used in dialogs/empty states). */
  compact?: boolean;
};

const STORAGE_KEY = "cp_input_mode";
const URL_RE = /^(https?:\/\/[^\s]+|www\.[^\s]+\.[^\s]+)/i;

const loadStoredMode = (): RecipeInputMode => {
  if (typeof window === "undefined") return "upload";
  const v = window.localStorage.getItem(STORAGE_KEY);
  if (v === "upload" || v === "link" || v === "text") return v;
  return "upload";
};

/** Heuristic: is the pasted content a URL? Tolerant of trailing whitespace. */
const looksLikeUrl = (s: string): boolean => {
  const t = s.trim();
  if (!t) return false;
  if (t.includes("\n")) return false; // multi-line → text
  if (t.length > 2048) return false;
  return URL_RE.test(t);
};

export const RecipeInput = ({
  headline = "Add a recipe",
  subhead = "Upload, paste or type — we'll organise it instantly",
  onSubmitUrl,
  onSubmitText,
  onSubmitFiles,
  enablePagePaste = true,
  initialUrl = "",
  initialText = "",
  forceMode,
  compact = false,
}: Props) => {
  const [mode, setMode] = useState<RecipeInputMode>(() =>
    forceMode ?? loadStoredMode(),
  );
  const [url, setUrl] = useState(initialUrl);
  const [text, setText] = useState(initialText);
  const [dragging, setDragging] = useState(false);

  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const urlRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const mobile = isMobile();

  // Persist the last-used input mode so a returning user lands back on the
  // method they prefer (e.g. people who always paste links don't have to
  // tap the Link tab every visit).
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Private browsing / quota — ignore.
    }
  }, [mode]);

  // Allow the parent to switch modes after mount (e.g. recovery action that
  // shoves the user into "Paste text" after a URL fetch failed).
  useEffect(() => {
    if (forceMode) setMode(forceMode);
  }, [forceMode]);

  // Auto-focus the relevant field when the user switches tabs. The drop-zone
  // doesn't get programmatic focus (it's a button, not a text field) — but
  // we move focus to it for keyboard users.
  useEffect(() => {
    if (mode === "link") urlRef.current?.focus();
    else if (mode === "text") textRef.current?.focus();
    else dropZoneRef.current?.focus({ preventScroll: true });
  }, [mode]);

  // Page-level paste auto-detect.
  // Only fires when no input/textarea/contenteditable is focused — otherwise
  // we'd hijack the user's normal paste-into-field behaviour.
  useEffect(() => {
    if (!enablePagePaste) return;
    const handler = (e: ClipboardEvent) => {
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || t?.isContentEditable) return;

      const data = e.clipboardData;
      if (!data) return;

      // File paste (e.g. screenshot from clipboard) → upload flow.
      const files = Array.from(data.files ?? []).filter(
        (f) => f.type.startsWith("image/") || f.type === "application/pdf",
      );
      if (files.length > 0) {
        e.preventDefault();
        setMode("upload");
        toast.success(files.length === 1 ? "Image detected" : `${files.length} images detected`);
        onSubmitFiles(files, false);
        return;
      }

      const pasted = data.getData("text");
      if (!pasted || !pasted.trim()) return;

      e.preventDefault();
      if (looksLikeUrl(pasted)) {
        setMode("link");
        setUrl(pasted.trim());
        toast.success("Recipe link detected");
        // Submit on next tick so the user sees the field populate first.
        setTimeout(() => onSubmitUrl(pasted.trim()), 80);
      } else {
        setMode("text");
        setText(pasted);
        toast.success("Recipe text detected");
        // Don't auto-submit text — give the user a chance to trim/review.
        setTimeout(() => textRef.current?.focus(), 80);
      }
    };
    window.addEventListener("paste", handler);
    return () => window.removeEventListener("paste", handler);
  }, [enablePagePaste, onSubmitFiles, onSubmitUrl]);

  // In-field paste handler for the URL input — if someone pastes a long block
  // of text into the URL field, gracefully shunt them to Text mode instead of
  // failing validation.
  const handleUrlPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text");
    if (pasted && !looksLikeUrl(pasted) && pasted.trim().length > 40) {
      e.preventDefault();
      setMode("text");
      setText(pasted);
      toast.success("Recipe text detected");
      setTimeout(() => textRef.current?.focus(), 80);
    }
  };

  const handleUrlSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const u = url.trim();
    if (!/^https?:\/\//i.test(u)) {
      toast.error("Please enter a valid URL starting with http(s)://");
      return;
    }
    onSubmitUrl(u);
  };

  const handleTextSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) {
      toast.error("Paste or type some recipe text first.");
      return;
    }
    onSubmitText(text);
  };

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const files = Array.from(e.dataTransfer.files ?? []);
      if (files.length > 0) onSubmitFiles(files, false);
      else toast.error("Please drop image or PDF files");
    },
    [onSubmitFiles],
  );

  const tabs: Array<{ id: RecipeInputMode; label: string; subtext: string; icon: typeof Upload }> = [
    { id: "upload", label: "Upload", subtext: "Photos, scans or PDF", icon: Upload },
    { id: "link", label: "Paste link", subtext: "From any recipe site", icon: Link2 },
    { id: "text", label: "Paste text", subtext: "Notes or ingredients", icon: FileText },
  ];

  return (
    <div
      className={cn(
        "mx-auto w-full max-w-2xl rounded-3xl border border-border bg-card shadow-soft",
        compact ? "p-4 sm:p-5" : "p-5 sm:p-7",
      )}
    >
      {/* Hidden file inputs */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        className="hidden"
        onChange={(e) => {
          const fs = Array.from(e.target.files ?? []);
          if (fs.length > 0) onSubmitFiles(fs, false);
          e.target.value = "";
        }}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const fs = Array.from(e.target.files ?? []);
          if (fs.length > 0) onSubmitFiles(fs, true);
          e.target.value = "";
        }}
      />

      {/* Header */}
      {(headline || subhead) && (
        <div className={cn("text-center", compact ? "mb-4" : "mb-5")}>
          {headline && (
            <h2 className="font-display text-2xl font-semibold leading-tight text-foreground sm:text-3xl">
              {headline}
            </h2>
          )}
          {subhead && (
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground sm:text-base">
              {subhead}
            </p>
          )}
        </div>
      )}

      {/* Segmented control */}
      <div
        role="tablist"
        aria-label="How to add a recipe"
        className="grid grid-cols-3 gap-1.5 rounded-2xl bg-muted/60 p-1.5"
      >
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = mode === t.id;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={active}
              type="button"
              onClick={() => setMode(t.id)}
              className={cn(
                "flex flex-col items-center justify-center gap-1 rounded-xl px-2 py-2.5 text-center transition-smooth sm:flex-row sm:gap-2 sm:py-3",
                active
                  ? "bg-card text-foreground shadow-soft ring-1 ring-border"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <Icon className={cn("h-4 w-4 shrink-0", active ? "text-primary" : "")} />
              <span className="flex flex-col items-center sm:items-start">
                <span className="text-xs font-semibold leading-tight sm:text-sm">{t.label}</span>
                <span className="hidden text-[11px] leading-tight text-muted-foreground sm:block">
                  {t.subtext}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Dynamic input area */}
      <div className={cn(compact ? "mt-4" : "mt-5")}>
        {mode === "upload" && (
          <div
            ref={dropZoneRef}
            role="button"
            tabIndex={0}
            onClick={() => fileRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                fileRef.current?.click();
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            className={cn(
              "group relative flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed bg-background/40 text-center transition-smooth hover:border-primary hover:bg-primary/5",
              compact ? "p-6" : "p-8 sm:p-10",
              dragging ? "border-primary bg-primary/5" : "border-border",
            )}
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary transition-smooth group-hover:bg-primary group-hover:text-primary-foreground">
              <Upload className="h-5 w-5" />
            </span>
            <div>
              <p className="font-display text-base font-semibold text-foreground sm:text-lg">
                Drag &amp; drop or click to upload
              </p>
              <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
                JPG, PNG or PDF — up to 8 pages
              </p>
            </div>
            <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <ImageIcon className="h-3 w-3" />
              We'll read the text from photos, screenshots & scanned pages
            </p>

            {mobile && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  cameraRef.current?.click();
                }}
                className="mt-2"
              >
                <Camera className="h-4 w-4" />
                Take a photo
              </Button>
            )}
          </div>
        )}

        {mode === "link" && (
          <form onSubmit={handleUrlSubmit} className="space-y-3">
            <div className="flex items-center gap-2 rounded-2xl border border-border bg-background/40 px-3 py-1 transition-smooth focus-within:border-primary">
              <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              <Input
                ref={urlRef}
                type="url"
                inputMode="url"
                autoComplete="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onPaste={handleUrlPaste}
                placeholder="Paste a recipe URL"
                className="border-0 bg-transparent px-0 text-base shadow-none focus-visible:ring-0"
              />
            </div>
            <Button type="submit" className="h-11 w-full" disabled={!url.trim()}>
              Clean recipe
            </Button>
          </form>
        )}

        {mode === "text" && (
          <form onSubmit={handleTextSubmit} className="space-y-3">
            <Textarea
              ref={textRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste or type your recipe"
              rows={6}
              className="min-h-[160px] resize-y rounded-2xl border-border bg-background/40 text-base focus-visible:border-primary focus-visible:ring-0"
            />
            <Button type="submit" className="h-11 w-full" disabled={!text.trim()}>
              Clean recipe
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};
